
import React, { useState, useEffect } from 'react';
import { Message as MessageType, Dialect, Attachment, AppMode, Language } from '../types';
import LiveModeUI from './Chat/LiveModeUI';
import ChatInputUI from './Chat/ChatInputUI';
import ChatMessagesUI from './Chat/ChatMessagesUI';

interface ChatAreaProps {
  messages: MessageType[];
  isLoading: boolean;
  onSendMessage: (content: string, attachments?: Attachment[]) => void;
  dialect: Dialect;
  mode: AppMode;
  isLiveActive?: boolean;
  setIsLiveActive?: (active: boolean) => void;
  voice: string;
  speechSpeed: number;
  language: Language;
  apiKey?: string;
  onOpenSettingsAt: (tab: 'profile' | 'app' | 'security') => void;
}

const ChatArea: React.FC<ChatAreaProps> = ({ 
  messages, 
  isLoading, 
  onSendMessage, 
  mode, 
  isLiveActive, 
  setIsLiveActive,
  voice,
  speechSpeed,
  language,
  apiKey,
  onOpenSettingsAt
}) => {
  const [isRecognitionActive, setIsRecognitionActive] = useState(false);

  useEffect(() => {
    if (mode === 'live' && isLiveActive && !isLoading && !isRecognitionActive) {
      const timer = setTimeout(() => startRecording(), 800);
      return () => clearTimeout(timer);
    }
  }, [mode, isLiveActive, isLoading, isRecognitionActive]);

  const startRecording = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = language === 'ar' ? 'ar-SA' : 'en-US';
    recognition.onstart = () => setIsRecognitionActive(true);
    recognition.onresult = (e: any) => {
      const transcript = e.results[0][0].transcript;
      if (transcript) onSendMessage(transcript);
    };
    recognition.onend = () => setIsRecognitionActive(false);
    try { recognition.start(); } catch (e) { setIsRecognitionActive(false); }
  };

  if (mode === 'live') {
    const assistantMessages = messages.filter(m => m.role === 'assistant');
    const lastMsg = assistantMessages[assistantMessages.length - 1];

    return (
      <LiveModeUI
        isLiveActive={isLiveActive || false}
        setIsLiveActive={setIsLiveActive}
        isRecognitionActive={isRecognitionActive}
        isLoading={isLoading}
        lastMessage={lastMsg}
        language={language}
        onStartLive={() => setIsLiveActive?.(true)}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#0f0f13]">
      <ChatMessagesUI 
        messages={messages} 
        isLoading={isLoading} 
        voice={voice} 
        speechSpeed={speechSpeed} 
        language={language} 
        apiKey={apiKey}
        onOpenSettingsAt={onOpenSettingsAt}
      />
      <ChatInputUI 
        onSendMessage={onSendMessage} 
        isLoading={isLoading} 
        language={language} 
      />
    </div>
  );
};

export default ChatArea;
